import { Button } from '@/Components/ui/button';
import GatedButton from '@/Components/Grants/GatedButton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogTitle,
} from '@/Components/ui/dialog';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * The confirmation an archive goes through.
 *
 * Archiving is final — nothing moves an archived passport anywhere — and it
 * always takes the whole subtree with it, whatever each descendant's status.
 * So before it asks, the dialog asks the server what the archive would take
 * (`passport.archive.preview`: the descendants per depth, their total, and
 * whether the count stopped at its cap) and names it. A passport that was
 * ever released stays public at its last release, which the dialog says too;
 * one that never was has nothing public to keep.
 *
 * Confirming posts `archived` to the status endpoint the release dialog uses.
 * The answer — with the run that archives the subtree, if there is one — goes
 * to `onDone`, which is how the status control picks up the run's badge.
 *
 * - `label`: the passport as the user knows it (its company ID or snowflake).
 * - `everReleased`: whether the passport has a release to stay public with.
 * - `unreleasedChanges`: a revision with edits since its last release; the
 *   dialog says those edits will never be published.
 * - `ability`: the permission the confirm is gated on — passports:write for a
 *   draft, releases:write for anything that left one.
 *
 * The preview is fetched on every open, like the release dialog's: a child
 * may have been created since the last one.
 */
export default function ArchiveDialog({
    open,
    onClose,
    snowflake,
    label = null,
    everReleased = false,
    unreleasedChanges = false,
    ontologyId = null,
    ability = 'passports:write',
    onDone,
}) {
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        let cancelled = false;

        setPreview(null);

        axios.get(route('passport.archive.preview', { snowflake }))
            .then(({ data }) => {
                if (!cancelled) {
                    setPreview(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    toast.error('Could not check what this archive would take with it');
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, snowflake]);

    const confirm = () => {
        setSaving(true);

        axios.post(route('passport.publish_status.update', { snowflake }), { publish_status: 'archived' })
            .then(({ data }) => {
                toast.success('Archived');
                onClose?.();
                onDone?.(data);
            })
            .catch((error) => {
                // A 409 carries the rule that refused it — a cascade already
                // running, a move the lifecycle does not allow.
                toast.error(error?.response?.data?.message || 'Failed to archive this passport');
            })
            .finally(() => setSaving(false));
    };

    const total = preview?.total ?? 0;
    const beneath = (preview?.descendants ?? [])
        .map((level) => `${level.name} ${level.count}`)
        .join(' · ');

    return (
        <Dialog open={open} onClose={(next) => { if (!next) onClose?.(); }}>
            <DialogContent className="max-w-lg">
                <DialogTitle>{`Archive ${label || snowflake}?`}</DialogTitle>
                <DialogDescription>Archiving is final and can't be undone.</DialogDescription>

                {!preview && (
                    <p className="text-sm text-muted-foreground">Checking what this archive would take with it…</p>
                )}

                {preview && total > 0 && (
                    <p className="text-sm text-gray-900">
                        It also archives the {total}{preview.truncated ? '+' : ''} {total === 1 ? 'passport' : 'passports'} beneath it ({beneath}).
                    </p>
                )}

                {everReleased && (
                    <p className="text-sm text-muted-foreground">Archived passports stay public, showing their last release.</p>
                )}

                {unreleasedChanges && (
                    <p className="text-sm text-muted-foreground">Its edits since the last release will never be published.</p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onClose?.()}>
                        Cancel
                    </Button>
                    <GatedButton
                        ability={ability}
                        ontologyId={ontologyId}
                        variant="destructive"
                        className="ml-2"
                        onClick={confirm}
                        disabled={saving || !preview}
                    >
                        Archive
                    </GatedButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
